import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

export interface Size {
  width: number;
  height: number;
}

/** Content-box size of an element, kept in sync with a ResizeObserver. */
export const useElementSize = (ref: RefObject<HTMLElement | null>): Size => {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      const next = { width: el.clientWidth, height: el.clientHeight };
      setSize(prev =>
        prev.width === next.width && prev.height === next.height ? prev : next
      );
    };
    read();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
};

export interface PagerState {
  page: number;
  pageCount: number;
  canPrev: boolean;
  canNext: boolean;
  prev: () => void;
  next: () => void;
  goTo: (page: number) => void;
}

/** Zero-based page state, clamped whenever the page count changes. */
export const usePager = (pageCount: number, initialPage = 0): PagerState => {
  const [page, setPage] = useState(initialPage);
  const last = Math.max(0, pageCount - 1);

  useEffect(() => {
    setPage(p => Math.min(Math.max(0, p), last));
  }, [last]);

  const goTo = useCallback(
    (p: number) => setPage(Math.min(Math.max(0, p), last)),
    [last]
  );
  const prev = useCallback(() => setPage(p => Math.max(0, p - 1)), []);
  const next = useCallback(() => setPage(p => Math.min(last, p + 1)), [last]);

  const current = Math.min(page, last);
  return {
    page: current,
    pageCount: Math.max(1, pageCount),
    canPrev: current > 0,
    canNext: current < last,
    prev,
    next,
    goTo,
  };
};

/**
 * Horizontal swipe + ←/→ keys on a region. Vertical gestures are ignored so
 * a bounded inner scroll (if any) keeps working.
 */
export const useSwipePaging = (
  ref: RefObject<HTMLElement | null>,
  onPrev: () => void,
  onNext: () => void
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    };
    const up = (e: PointerEvent) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) < 56 || Math.abs(dy) > Math.abs(dx) * 0.6) return;
      if (dx < 0) onNext();
      else onPrev();
    };
    const cancel = () => {
      tracking = false;
    };
    const key = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [role="tablist"]')) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrev();
      }
    };

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', cancel);
    el.addEventListener('keydown', key);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
      el.removeEventListener('keydown', key);
    };
  }, [ref, onPrev, onNext]);
};

/**
 * Publishes an element's rendered height (e.g. the top bar, including its
 * safe-area padding) as a CSS custom property on `<html>` so bounded screens
 * can size themselves as `100dvh - var(--bh-topbar-h)` instead of a
 * hard-coded guess.
 */
export const usePublishHeight = (
  ref: RefObject<HTMLElement | null>,
  cssVar: string
) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const write = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty(cssVar, `${h}px`);
    };
    write();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(write);
      ro.observe(el);
    }
    window.addEventListener('resize', write);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', write);
    };
  }, [ref, cssVar]);
};
